import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Play, Zap, HeartPulse, Salad, Video, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1605296867304-46d5465a13f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxmaXRuZXNzJTIwdHJhaW5lciUyMGd5bSUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc5MDEwNzk2NXww&ixlib=rb-4.1.0&q=85";
const YOGA = "https://images.pexels.com/photos/10895295/pexels-photo-10895295.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const MEAL = "https://images.unsplash.com/photo-1543352632-5a4b24e4d2a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxoZWFsdGh5JTIwbWVhbCUyMHByZXAlMjBib3dsfGVufDB8fHx8MTc5MDEwNzk2NHww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const features = [
    { icon: Video, title: "Live 1-on-1 Studio", desc: "Real WebRTC video rooms with pre-call checks, timers & live heart-rate overlay." },
    { icon: Zap, title: "AI Form Checker", desc: "Upload a clip — get instant form scoring and corrective cues from AI vision." },
    { icon: Salad, title: "Smart Nutrition", desc: "AI macro meal plans that auto-generate aisle-by-aisle grocery lists." },
    { icon: HeartPulse, title: "Wearable Sync", desc: "Pull heart rate, calories, steps & sleep from Apple Watch, Fitbit & Garmin." },
  ];

  return (
    <div className="min-h-screen bg-[#121212] text-white relative z-10">
      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#121212]/80 border-b border-[#2A2A2A]">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo size={36} />
            <span className="font-display font-black text-xl tracking-tight">COMMUNITY<span className="text-[#FF6B6B]">PUMP</span></span>
          </div>
          <button
            data-testid="landing-signin-btn"
            onClick={() => navigate(user ? "/feed" : "/auth")}
            className="px-5 py-2 rounded-full bg-[#FF6B6B] text-black font-semibold text-sm hover:brightness-110 transition-all"
          >
            {user ? "Enter App" : "Sign In"}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#121212] via-[#121212]/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 py-24 md:py-36">
          <div className="max-w-2xl animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF6B6B]/30 bg-[#FF6B6B]/5 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#FF6B6B] animate-pulse" />
              <span className="text-xs uppercase tracking-[0.2em] text-[#FF6B6B]">The global fitness network</span>
            </div>
            <h1 className="font-display font-black text-5xl md:text-7xl uppercase tracking-wide leading-[0.95]">
              Train harder.<br />Coach smarter.<br /><span className="text-[#FF6B6B]">Connect</span> globally.
            </h1>
            <p className="mt-6 text-lg text-[#A0A0A0] max-w-lg leading-relaxed">
              The premium marketplace where world-class coaches meet driven athletes. Workouts, meal plans, mindfulness & live 1-on-1 training — all in one beautifully minimal app.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid="hero-train-btn"
                onClick={() => navigate("/auth?role=learner")}
                className="group px-6 py-3 rounded-full bg-[#FF6B6B] text-black font-bold uppercase tracking-wide text-sm flex items-center gap-2 hover:brightness-110 transition-all"
              >
                I Want To Train <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                data-testid="hero-coach-btn"
                onClick={() => navigate("/auth?role=instructor")}
                className="px-6 py-3 rounded-full border border-[#4ECCD3]/40 text-[#4ECCD3] font-bold uppercase tracking-wide text-sm flex items-center gap-2 hover:bg-[#4ECCD3]/10 transition-colors"
              >
                <Play className="w-4 h-4" /> I Want To Coach
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section className="max-w-[1400px] mx-auto px-6 py-20">
        <h2 className="font-display font-bold text-3xl md:text-4xl uppercase tracking-tight mb-2">Everything you need. <span className="text-[#FF6B6B]">Nothing you don't.</span></h2>
        <p className="text-[#A0A0A0] mb-10 max-w-xl">Built for performance, monetization and connection across every timezone.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f, i) => (
            <div key={i} className="group bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-6 hover:-translate-y-1 hover:border-[#FF6B6B]/50 transition-all duration-200">
              <div className="w-12 h-12 rounded-xl bg-[#FF6B6B]/10 flex items-center justify-center mb-4 group-hover:bg-[#FF6B6B]/20 transition-colors">
                <f.icon className="w-6 h-6 text-[#FF6B6B]" />
              </div>
              <h3 className="font-bold text-lg mb-2">{f.title}</h3>
              <p className="text-sm text-[#A0A0A0] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Dual role */}
      <section className="max-w-[1400px] mx-auto px-6 py-10 grid md:grid-cols-2 gap-4">
        <div className="relative rounded-3xl overflow-hidden border border-[#2A2A2A] min-h-[280px] group">
          <img src={YOGA} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-60 transition-opacity" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
          <div className="relative p-8 flex flex-col justify-end h-full min-h-[280px]">
            <span className="text-xs uppercase tracking-[0.2em] text-[#4ECCD3] mb-2">For Members</span>
            <h3 className="font-display font-bold text-2xl uppercase tracking-tight">Your personal fitness universe</h3>
            <p className="text-sm text-[#A0A0A0] mt-2">Feed, progress charts, streaks, challenges and 1-on-1 booking.</p>
          </div>
        </div>
        <div className="relative rounded-3xl overflow-hidden border border-[#2A2A2A] min-h-[280px] group">
          <img src={MEAL} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-60 transition-opacity" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
          <div className="relative p-8 flex flex-col justify-end h-full min-h-[280px]">
            <span className="text-xs uppercase tracking-[0.2em] text-[#FF6B6B] mb-2">For Coaches</span>
            <h3 className="font-display font-bold text-2xl uppercase tracking-tight">Monetize your expertise</h3>
            <p className="text-sm text-[#A0A0A0] mt-2">Membership tiers, tip jars, MRR analytics and a full client CRM.</p>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#2A2A2A] mt-10">
        <div className="max-w-[1400px] mx-auto px-6 py-8 flex items-center justify-between text-sm text-[#A0A0A0]">
          <span>© 2026 CommunityPump</span>
          <span>Sent globally. Trained locally.</span>
        </div>
      </footer>
    </div>
  );
}
